<script setup lang="ts">
/**
 * Screen Reader Announcer Component
 *
 * This component provides an ARIA live region for announcing
 * dynamic content changes to screen reader users. It uses the
 * useAnnouncer composable to receive announcements from anywhere
 * in the application.
 *
 * Place this component at the root of your application (App.vue)
 * and use the useAnnouncer composable to make announcements.
 *
 * @example
 * ```vue
 * // In App.vue
 * <template>
 *   <ScreenReaderAnnouncer />
 *   <RouterView />
 * </template>
 *
 * // In any component
 * import { useAnnouncer } from '@/composables/useAnnouncer';
 *
 * const { announce } = useAnnouncer();
 * announce('Form submitted successfully');
 * ```
 */

import { useAnnouncer } from '@/composables/useAnnouncer';

const { message, politeness } = useAnnouncer();
</script>

<template>
  <!--
    Two separate live regions for polite and assertive announcements.
    This ensures that the correct politeness level is always used.
  -->

  <!-- Polite announcements (wait for user to finish reading current content) -->
  <div
    v-show="politeness === 'polite'"
    role="status"
    aria-live="polite"
    aria-atomic="true"
    class="sr-only"
  >
    {{ politeness === 'polite' ? message : '' }}
  </div>

  <!-- Assertive announcements (interrupt and announce immediately) -->
  <div
    v-show="politeness === 'assertive'"
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
    class="sr-only"
  >
    {{ politeness === 'assertive' ? message : '' }}
  </div>
</template>

<style scoped>
/*
 * Screen reader only styles.
 * Visually hidden but accessible to screen readers.
 */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
